/**
 * REST client for the analytics API (dash-api `/api/analytics/**`).
 * Accessible by ROLE_ADMIN and ROLE_MANAGER. Used by the manager-app Analytics page.
 */

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token
        ? {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

/**
 * Aggregated user activity metrics.
 * @param {string} recent recent-window code (e.g. '1d', '1w', '30d', '1M')
 * @returns {Promise<object>} UserActivitySummaryDto
 */
export async function getUserActivitySummary(recent = '1w') {
    const params = new URLSearchParams({dateMode: 'recent', recent});
    const resp = await fetch(`${BASE}/api/analytics/user-activity/summary?${params}`, {
        headers: authHeaders(),
    });
    if (!resp.ok) throw new Error(`getUserActivitySummary ${resp.status}`);
    return resp.json();
}

/**
 * Time-bucketed LLM spend / call / token series.
 * @param {string} recent recent-window code
 * @returns {Promise<object>} LlmAdminTimelineResponse
 */
export async function getLlmTimeline(recent = '1w') {
    const params = new URLSearchParams({dateMode: 'recent', recent});
    const resp = await fetch(`${BASE}/api/analytics/timeline?${params}`, {
        headers: authHeaders(),
    });
    if (!resp.ok) throw new Error(`getLlmTimeline ${resp.status}`);
    return resp.json();
}

/**
 * Per-user activity breakdown (User Activity Table).
 * @param {string} recent recent-window code
 * @returns {Promise<object>} UserActivityByUserDto ({ users: [...] })
 */
export async function getUserActivityByUser(recent = '1w') {
    const params = new URLSearchParams({dateMode: 'recent', recent});
    const resp = await fetch(`${BASE}/api/analytics/user-activity/by-user?${params}`, {
        headers: authHeaders(),
    });
    if (!resp.ok) throw new Error(`getUserActivityByUser ${resp.status}`);
    return resp.json();
}

/**
 * Report-production pipeline counts (drafts, completed FINTELs, SITREP types).
 * @returns {Promise<object>} ReportStatsDto
 */
export async function getReportStats() {
    const resp = await fetch(`${BASE}/api/analytics/report-stats`, {
        headers: authHeaders(),
    });
    if (!resp.ok) throw new Error(`getReportStats ${resp.status}`);
    return resp.json();
}

/**
 * AI assistant and chat activity metrics.
 * @param {string} recent recent-window code (e.g. '1d', '1w', '30d')
 * @returns {Promise<object>} AiActivitySummaryDto
 */
export async function getAiActivity(recent = '1w') {
    const params = new URLSearchParams({dateMode: 'recent', recent});
    const resp = await fetch(`${BASE}/api/analytics/ai-activity?${params}`, {
        headers: authHeaders(),
    });
    if (!resp.ok) throw new Error(`getAiActivity ${resp.status}`);
    return resp.json();
}
